import React, { FC } from 'react'
import { PromoProps } from './Promo.types'
import { ImageCompound } from '../../Compounds/src/ImageCompound'
import { BrandIcon } from '../../Compounds/src/BrandIconCompound'
import { VideoCompound } from '../../Compounds/src/VideoCompound'
import { LinkCompound } from '../../Compounds/src/LinkCompound'
import { RTECompound } from '../../Compounds/src/RTECompound'
import { getValueListBgColor } from '../../../utils/background-util'
import { sanitizeHTML } from '../../../utils/common-utils'
import { ErrorBoundary } from '../../ErrorBoundary'

/**
 * Promo component for displaying promotional content blocks.
 *
 * @remarks
 * Renders a flexible promotional section that can include images, icons, video, eyebrow text, rich text, and links.
 * Supports various layout options such as background color, column sizing, vertical spacing, media alignment, and brand extension.
 * Handles conditional rendering of media and content based on the provided props.
 *
 * @param props - The properties for the Promo component.
 * @param props.testId - Optional test id for testing.
 * @param props.content - The content and configuration for the promo block.
 * @returns The rendered Promo component or null if no content is provided.
 */
export const Promo: FC<PromoProps> = props => {
  const { testId, content } = props

  if (!content) {
    return null
  }

  const {
    imageLocation,
    eyebrowImage,
    icon,
    image,
    eyebrowText,
    contentText,
    linkText,
    video,
    backgroundColor,
    verticalSpace,
    columnSize,
    brandExtension,
    enableMediaBorder,
    enableMediaPadding,
    heading,
  } = content

  const bgColor = backgroundColor ? `${getValueListBgColor(backgroundColor)}` : ''
  const isWhiteBg = bgColor === 'u-bgColorWhite'
  const colSize = columnSize ? `u-size${columnSize}` : ''
  const mediaSideClass = imageLocation === 'left' ? 'Promo-mediaLeft' : 'Promo-mediaRight'
  const toBoolean = (val: unknown): boolean => String(val).trim().toLowerCase() === 'true'
  const hasBorder = toBoolean(enableMediaBorder)
  const hasPadding = toBoolean(enableMediaPadding)
  const verticalPadding = verticalSpace ?? ''
  const centerMargin = !eyebrowImage?.src && !icon?.iconsDropdown ? 'contentMargin' : ''
  const hasVideo = Boolean(video?.playlistId?.length)
  const hasImage = Boolean(image?.src?.length)
  const hasMediaContent = hasVideo || hasImage
  const hasEyebrowImage = Boolean(eyebrowImage?.src?.length)
  const hasIcon = Boolean(icon?.iconsDropdown?.length)
  const hasEyebrowMedia = hasEyebrowImage || hasIcon
  const eyebrowTextPaddingClass = hasEyebrowMedia ? 'u-paddingBottom2gu' : 'u-paddingVert2gu'
  const getMediaContent = () => {
    if (video?.playlistId?.length) {
      return (
        <ErrorBoundary fallback={<div>Video failed to load</div>}>
          <VideoCompound {...video} />
        </ErrorBoundary>
      )
    }
    return (
      <ErrorBoundary>
        <ImageCompound {...image} />
      </ErrorBoundary>
    )
  }

  return (
    <section
      className={`u-sizeConstrained ${verticalPadding} ${
        colSize ? `${colSize} u-md-sizeFull u-sm-sizeFull u-marginCenter` : 'u-sizeFull'
      } ${brandExtension}`}
    >
      <div
        className={`${bgColor} u-borderRadius24`}
        data-testid={testId ? `${testId}-container` : null}
      >
        <div
          className={`Promo u-flex u-md-flex u-md-flexCol u-md-flexColReverse u-sm-flex u-sm-flexCol u-sm-flexColReverse ${
            imageLocation === 'left' ? 'u-flexRowReverse' : 'u-flexRow'
          } ${mediaSideClass}`}
          data-testid={testId ? `${testId}-inner` : null}
        >
          <div
            className={`first-column ${
              hasMediaContent ? 'u-size6of12' : 'u-sizeFull'
            } u-md-sizeFull u-sm-sizeFull u-paddingVert32 u-paddingHoriz24 ${centerMargin}`}
            data-testid={testId ? `${testId}-left-column` : null}
          >
            {hasEyebrowImage ? (
              <div className='u-paddingBottom20'>
                <ImageCompound {...eyebrowImage} />
              </div>
            ) : (
              hasIcon && <BrandIcon {...icon} />
            )}
            {eyebrowText && eyebrowText?.length > 0 && (
              <div
                className={`Promo-eyebrowText Type-5 ${eyebrowTextPaddingClass}`}
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(eyebrowText) }}
              ></div>
            )}
            {heading && heading?.length > 0 && (
              <h3
                className='u-textColorNeutral u-textSizeMsrp4 u-paddingBottom2gu'
                dangerouslySetInnerHTML={{ __html: sanitizeHTML(heading) }}
              ></h3>
            )}
            {contentText?.content && (
              <div className='u-paddingBottom32'>
                <RTECompound {...contentText} />
              </div>
            )}
            {linkText?.text?.length && <LinkCompound {...linkText} />}
          </div>
          {hasMediaContent && (
            <div
              className={`second-column u-lg-flex u-size6of12 u-md-sizeFull u-sm-sizeFull 
              ${hasBorder ? 'u-border' : ''} 
              ${hasPadding && !isWhiteBg ? 'u-padding5gu' : ''} 
              ${hasVideo ? 'u-flexAlignItemsCenter' : ''}
              ${isWhiteBg ? 'Promo-whiteBg u-borderRadius24' : ''}`}
              data-testid={testId ? `${testId}-right-column` : null}
            >
              {getMediaContent()}
            </div>
          )}
        </div>
      </div>
    </section>
  )
}
